import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-phone-find-line',
    title: 'AI Lead Capture',
    description:
      'AI answers every call and web inquiry 24/7—qualifying and routing each prospect before your team ever picks up.',
    features: ['AI call answering', 'SMS lead capture', 'Instant qualification', 'Zero missed opportunities'],
    size: 'large',
  },
  {
    icon: 'ri-calendar-2-line',
    title: 'Smart Scheduling',
    description:
      'Real-time calendar sync, automated reminders, and optimized dispatch—jobs booked without back-and-forth.',
    features: ['Real-time booking', 'Calendar integration', 'Automated reminders', 'Dispatch coordination'],
    size: 'normal',
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Automated Follow-Up',
    description:
      'Multi-step sequences handle missed calls, estimates, and re-engagement—automatically, every time.',
    features: ['Missed call text-back', 'Estimate follow-up', 'Re-engagement', 'Review requests'],
    size: 'normal',
  },
  {
    icon: 'ri-dashboard-3-line',
    title: 'Pipeline Dashboard',
    description:
      'See every lead and job in real time. Know exactly where revenue stands—and make decisions accordingly.',
    features: ['Lead tracking', 'Job visibility', 'Conversion insights', 'Performance metrics'],
    size: 'large',
  },
];

export default function ServicesSection() {
  return (
    <section className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <p className="text-[#6E6E73] text-sm font-semibold uppercase tracking-widest mb-5">
              What We Build
            </p>
            <h2 className="text-5xl lg:text-6xl font-extrabold text-[#0A0A0A] leading-none tracking-tighter">
              Four systems.
              <br />
              <span className="text-[#6E6E73] font-light">One outcome.</span>
            </h2>
          </div>
          <Link
            to="/services"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#1D1D1F] transition-colors cursor-pointer self-start lg:self-auto"
          >
            Explore all services
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Card 1 — large (spans 7 cols) */}
          <div className="lg:col-span-7 bg-[#F5F5F7] rounded-3xl p-10 group hover:bg-[#EBEBF0] transition-colors">
            <div className="w-12 h-12 flex items-center justify-center bg-white rounded-2xl mb-8 group-hover:scale-110 transition-transform">
              <i className={`${services[0].icon} text-[#0A0A0A] text-xl`} />
            </div>
            <h3 className="text-[#0A0A0A] text-2xl font-bold mb-3 tracking-tight">{services[0].title}</h3>
            <p className="text-[#6E6E73] text-base leading-relaxed mb-8 max-w-md">{services[0].description}</p>
            <div className="grid grid-cols-2 gap-2">
              {services[0].features.map((feat) => (
                <div key={feat} className="flex items-center gap-2 text-sm text-[#1D1D1F]">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-[#0A0A0A] text-xs font-bold" />
                  </div>
                  {feat}
                </div>
              ))}
            </div>
          </div>

          {/* Card 2 — normal (spans 5 cols) */}
          <div className="lg:col-span-5 bg-[#0A0A0A] rounded-3xl p-10 group">
            <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-2xl mb-8 group-hover:bg-white/15 transition-colors">
              <i className={`${services[1].icon} text-white text-xl`} />
            </div>
            <h3 className="text-white text-2xl font-bold mb-3 tracking-tight">{services[1].title}</h3>
            <p className="text-white/55 text-base leading-relaxed mb-8">{services[1].description}</p>
            <ul className="space-y-2">
              {services[1].features.map((feat) => (
                <li key={feat} className="flex items-center gap-2 text-sm text-white/70">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-white/40 text-xs" />
                  </div>
                  {feat}
                </li>
              ))}
            </ul>
          </div>

          {/* Card 3 — normal (spans 5 cols) */}
          <div className="lg:col-span-5 bg-[#F5F5F7] rounded-3xl p-10 group hover:bg-[#EBEBF0] transition-colors">
            <div className="w-12 h-12 flex items-center justify-center bg-white rounded-2xl mb-8 group-hover:scale-110 transition-transform">
              <i className={`${services[2].icon} text-[#0A0A0A] text-xl`} />
            </div>
            <h3 className="text-[#0A0A0A] text-2xl font-bold mb-3 tracking-tight">{services[2].title}</h3>
            <p className="text-[#6E6E73] text-base leading-relaxed mb-8">{services[2].description}</p>
            <ul className="space-y-2">
              {services[2].features.map((feat) => (
                <li key={feat} className="flex items-center gap-2 text-sm text-[#1D1D1F]">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-[#0A0A0A] text-xs font-bold" />
                  </div>
                  {feat}
                </li>
              ))}
            </ul>
          </div>

          {/* Card 4 — large (spans 7 cols) with amber accent */}
          <div
            className="lg:col-span-7 rounded-3xl p-10 group"
            style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1e293b 100%)' }}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-2xl mb-8 group-hover:bg-amber-500/30 transition-colors">
              <i className={`${services[3].icon} text-amber-400 text-xl`} />
            </div>
            <h3 className="text-white text-2xl font-bold mb-3 tracking-tight">{services[3].title}</h3>
            <p className="text-white/55 text-base leading-relaxed mb-8 max-w-md">{services[3].description}</p>
            <div className="grid grid-cols-2 gap-2">
              {services[3].features.map((feat) => (
                <div key={feat} className="flex items-center gap-2 text-sm text-white/70">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-amber-400 text-xs" />
                  </div>
                  {feat}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
